import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { OutletRevenueOut } from '../api/types'
import { matchPreset, PRESETS } from '../lib/dates'
import { useFilters, type Compare, type Scope } from '../lib/filters'

/**
 * The outlet list is a side effect of the by-outlet revenue endpoint — there is
 * no dedicated outlets endpoint, and this one already returns every outlet that
 * has sales. A wide window keeps outlets from vanishing out of the filter on a
 * quiet week.
 */
function useOutlets() {
  return useQuery({
    queryKey: ['outlets'],
    staleTime: 60 * 60 * 1000,
    queryFn: () =>
      api.get<OutletRevenueOut>('/analytics/revenue/by-outlet', {
        from_date: '2020-01-01',
        to_date: new Date().toISOString().slice(0, 10),
        compare: 'none',
        scope: 'all',
      }),
  })
}

export function FilterBar({
  showCompare = false,
  allTime = false,
}: {
  showCompare?: boolean
  /** No dates means all time here, with a chip to get back to it. */
  allTime?: boolean
}) {
  const { filters, update } = useFilters({ allTime })

  return (
    <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
      <DateRangeFields allTime={allTime} />
      <OutletField allTime={allTime} />

      <div className="field">
        <label htmlFor="scope">Counting</label>
        <select
          id="scope"
          value={filters.scope}
          onChange={(event) => update({ scope: event.target.value as Scope })}
        >
          <option value="all">All sales</option>
          <option value="fulfilled">Fulfilled only</option>
        </select>
      </div>

      {showCompare ? (
        <div className="field">
          <label htmlFor="compare">Compare with</label>
          <select
            id="compare"
            value={filters.compare}
            onChange={(event) => update({ compare: event.target.value as Compare })}
          >
            <option value="none">No comparison</option>
            <option value="previous_period">Previous period</option>
            <option value="previous_year">Same period last year</option>
          </select>
        </div>
      ) : null}
    </div>
  )
}

/** The outlet picker on its own, for pages with no sales scope. */
export function OutletField({ allTime = false }: { allTime?: boolean }) {
  const { filters, update } = useFilters({ allTime })
  const outlets = useOutlets()

  return (
    <div className="field">
      <label htmlFor="outlet">Outlet</label>
      <select
        id="outlet"
        value={filters.outletId ?? ''}
        onChange={(event) =>
          update({ outletId: event.target.value ? Number(event.target.value) : null })
        }
      >
        <option value="">All outlets</option>
        {(outlets.data?.items ?? [])
          .filter((row) => row.outlet_id !== null)
          .map((row) => (
            <option key={row.outlet_id} value={row.outlet_id!}>
              {row.outlet_name ?? `Outlet ${row.outlet_id}`}
            </option>
          ))}
      </select>
    </div>
  )
}

/**
 * The preset buttons and from/to dates on their own, for pages whose other
 * filters differ — the delivery pages, which coordinators use and which have
 * no outlet or sales scope.
 */
export function DateRangeFields({ allTime = false }: { allTime?: boolean }) {
  const { filters, update } = useFilters({ allTime })
  const unbounded = filters.from === null && filters.to === null
  const active =
    filters.from !== null && filters.to !== null
      ? matchPreset({ from: filters.from, to: filters.to })
      : null

  return (
    <>
      <div className="seg">
        {allTime ? (
          <button className={unbounded ? 'on' : ''} onClick={() => update({ from: null, to: null })}>
            All time
          </button>
        ) : null}
        {PRESETS.map((preset) => (
          <button
            key={preset.key}
            className={active === preset.key ? 'on' : ''}
            onClick={() => update(preset.range())}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="field">
        <label htmlFor="from">From</label>
        <input
          id="from"
          type="date"
          value={filters.from ?? ''}
          max={filters.to ?? undefined}
          // Cleared: an open end on an all-time page, the default elsewhere.
          onChange={(event) => update({ from: event.target.value || null })}
        />
      </div>

      <div className="field">
        <label htmlFor="to">To</label>
        <input
          id="to"
          type="date"
          value={filters.to ?? ''}
          min={filters.from ?? undefined}
          onChange={(event) => update({ to: event.target.value || null })}
        />
      </div>
    </>
  )
}
